import { useEffect, useState } from 'react';
import { KeenIcon } from '@/components';
import clsx from 'clsx';
import Select, { MultiValue } from 'react-select';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import { ErrorApiResponse } from '@/types/api.types';
import { DMORegisterForm } from '@/types/dmo-management.types';
import {
  useDirectDMORegistrationMutation,
  useCompleteDMORegistrationMutation,
  useUpdateRegisteredDMOMutation,
} from '@/services/DMOManagementService';
import { useGetRegionsQuery } from '@/services/RegionManagementService';
import { Statuses } from '@/constant';

interface RegionOption {
  value: string;
  label: string;
}

interface DMORegistrationFormProps {
  initialData?: Partial<DMORegisterForm>;
  userId?: string; // This is the 'id' in the route, used for update
  token?: string;
  isEditMode?: boolean;
  onSuccess?: () => void;
  onCancel?: () => void;
  isUserLoading?: boolean;
  showToolbar?: boolean;
  showLegalConsent?: boolean;
}

const DMORegistrationForm = ({
  initialData,
  userId,
  token,
  isEditMode = false,
  onSuccess,
  onCancel,
  isUserLoading = false,
  showToolbar = true,
  showLegalConsent = false,
}: DMORegistrationFormProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const [directRegistration, { isLoading: isDirectSubmitting }] =
    useDirectDMORegistrationMutation();
  const [completeRegistration, { isLoading: isCompleteSubmitting }] =
    useCompleteDMORegistrationMutation();
  const [updateDMO, { isLoading: isUpdateSubmitting }] =
    useUpdateRegisteredDMOMutation();

  const isSubmitting =
    isDirectSubmitting || isCompleteSubmitting || isUpdateSubmitting;

  const [form, setForm] = useState<DMORegisterForm>({
    organisation_name: '',
    organisation_type: '',
    contact_email: '',
    linked_regions: [],
    public_homepage: '',
    description: '',
    language: 'en',
    email: '',
    name: '',
    has_accepted_terms: false,
    sendTermsPdf: false,
  });

  const {
    data: regionsData,
    isLoading: isRegionsLoading,
    isError: isRegionsError,
  } = useGetRegionsQuery({
    page: 1,
    limit: 100,
    status: Statuses.ACTIVE,
    language: form.language,
  });

  const regionOptions: RegionOption[] =
    regionsData?.data?.map((region) => ({
      value: region.id,
      label: region.name,
    })) ?? [];

  const [errors, setErrors] = useState<
    Partial<Record<keyof DMORegisterForm, string>>
  >({});
  const [profilePicFile, setProfilePicFile] = useState<File | null>(null);

  useEffect(() => {
    if (initialData) {
      setForm((prev) => ({
        ...prev,
        ...initialData,
      }));
    }
  }, [initialData]);

  const handleChange = (
    e:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLTextAreaElement>
      | React.ChangeEvent<HTMLSelectElement>
  ) => {
    const { name, value, type, checked } = e.target as HTMLInputElement;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const handleRegionsChange = (newValue: MultiValue<RegionOption>) => {
    const selectedRegionIds = newValue.map((option) => option.value);
    setForm((prev) => ({ ...prev, linked_regions: selectedRegionIds }));
    setErrors((prev) => ({ ...prev, linked_regions: undefined }));
  };

  const validate = () => {
    const newErrors: Partial<Record<keyof DMORegisterForm, string>> = {};

    if (!form.organisation_name.trim()) {
      newErrors.organisation_name = 'Organisation name is required';
    }
    if (!form.organisation_type.trim()) {
      newErrors.organisation_type = 'Organisation type is required';
    }
    if (!form.contact_email.trim()) {
      newErrors.contact_email = 'Contact email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.contact_email.trim())) {
      newErrors.contact_email = 'Please enter a valid email';
    }
    if (!form.email.trim()) {
      newErrors.email = 'DMO user email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      newErrors.email = 'Please enter a valid DMO user email';
    }
    if (!form.description.trim()) {
      newErrors.description = 'Description is required';
    }
    if (showLegalConsent && !form.has_accepted_terms) {
      newErrors.has_accepted_terms =
        'You must accept the terms and conditions';
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) {
      enqueueSnackbar('Please fix the form errors', { variant: 'error' });
      return;
    }

    const linkedRegions = form.linked_regions;

    const formData = new FormData();
    formData.append('organisation_name', form.organisation_name.trim());
    formData.append('organisation_type', form.organisation_type.trim());
    formData.append('contact_email', form.contact_email.trim());
    linkedRegions.forEach((regionId) => {
      formData.append('linked_regions[]', regionId);
    });
    if (form.public_homepage.trim()) {
      formData.append('public_homepage', form.public_homepage.trim());
    }
    formData.append('description', form.description.trim());
    formData.append('language', form.language);
    formData.append('email', form.email.trim());
    if (form.name.trim()) {
      formData.append('name', form.name.trim());
    }
    if (profilePicFile) {
      formData.append('profile_pic', profilePicFile);
    }
    if (showLegalConsent) {
      formData.append(
        'has_accepted_terms',
        String(form.has_accepted_terms)
      );
      formData.append('sendTermsPdf', String(form.sendTermsPdf));
    }

    try {
      if (token) {
        await completeRegistration({
          token,
          data: formData,
          lang: form.language,
        }).unwrap();
        enqueueSnackbar('DMO user registered successfully', {
          variant: 'success',
        });
      } else if (isEditMode && userId) {
        await updateDMO({
          id: userId,
          data: formData,
          lang: form.language,
        }).unwrap();
        enqueueSnackbar('DMO details updated successfully', {
          variant: 'success',
        });
      } else {
        await directRegistration({
          data: formData,
          lang: form.language,
        }).unwrap();
        enqueueSnackbar('DMO user registered successfully', {
          variant: 'success',
        });
      }

      if (onSuccess) {
        onSuccess();
      } else {
        navigate('/dmo-management');
      }
    } catch (err) {
      const message =
        (err as ErrorApiResponse)?.data?.message ||
        (err as ErrorApiResponse)?.message ||
        `Failed to ${isEditMode ? 'update' : 'register'} DMO user`;
      enqueueSnackbar(message, { variant: 'error' });
    }
  };
  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      navigate('/dmo-management');
    }
  };

  return (
    <div className="flex flex-col gap-5">
      {showToolbar && (
        <div className="flex items-center justify-between flex-wrap gap-5">
          <div className="flex flex-col justify-center gap-1">
            <h1 className="text-xl font-semibold leading-none text-gray-900">
              {isEditMode ? 'Edit DMO' : 'Register New DMO'}
            </h1>
            <p className="text-sm font-medium text-gray-500">
              {isEditMode
                ? 'Update DMO organisation and contact.'
                : 'Register a new DMO organisation and contact.'}
            </p>
          </div>
          <div className="flex items-center gap-2.5">
            <button
              type="button"
              className="btn btn-sm btn-secondary flex items-center gap-2"
              onClick={handleCancel}
            >
              <KeenIcon type="filled" icon="cross" className="text-white" />
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-sm btn-primary flex items-center gap-2"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              <KeenIcon type="filled" icon="check" className="text-white" />
              {isSubmitting ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      )}

      <div className="card card-grid w-full border-none">
        <div className="card-body space-y-6">
          {isEditMode && isUserLoading && (
            <p className="text-sm text-gray-500">Loading DMO user details...</p>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="organisation_name"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Organisation Name <span className="text-red-500">*</span>
              </label>
              <input
                id="organisation_name"
                type="text"
                name="organisation_name"
                value={form.organisation_name}
                onChange={handleChange}
                className={clsx(
                  'input input-md w-full',
                  errors.organisation_name &&
                    'border-red-500 focus:ring-red-500'
                )}
                autoComplete="organization"
                placeholder="Coastal Destinations Inc."
                aria-label="Organisation Name"
              />
              {errors.organisation_name && (
                <p className="text-xs text-red-600 mt-1">
                  {errors.organisation_name}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="organisation_type"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Organisation Type <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="organisation_type"
                name="organisation_type"
                value={form.organisation_type}
                onChange={handleChange}
                className={clsx(
                  'input input-md w-full',
                  errors.organisation_type &&
                    'border-red-500 focus:ring-red-500'
                )}
                autoComplete="off"
                placeholder="Tourism Board"
                aria-label="Organisation Type"
              />
              {errors.organisation_type && (
                <p className="text-xs text-red-600 mt-1">
                  {errors.organisation_type}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="contact_email"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Contact Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                id="contact_email"
                name="contact_email"
                value={form.contact_email}
                onChange={handleChange}
                className={clsx(
                  'input input-md w-full',
                  errors.contact_email && 'border-red-500 focus:ring-red-500'
                )}
                autoComplete="email"
                placeholder="contact@coastal.com"
                aria-label="Contact Email"
              />
              {errors.contact_email && (
                <p className="text-xs text-red-600 mt-1">
                  {errors.contact_email}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="public_homepage"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Public Homepage
              </label>
              <input
                type="url"
                id="public_homepage"
                name="public_homepage"
                value={form.public_homepage}
                autoComplete="off"
                onChange={handleChange}
                className="input input-md w-full"
                placeholder="https://www.coastal.com"
                aria-label="Public Homepage"
              />
            </div>
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                DMO User Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                name="email"
                id="email"
                value={form.email}
                autoComplete="email"
                onChange={handleChange}
                placeholder="admin@dmo-example.com"
                readOnly={!!token}
                className={clsx(
                  'input input-md w-full',
                  token && 'bg-gray-100 cursor-not-allowed',
                  errors.email && 'border-red-500 focus:ring-red-500'
                )}
                aria-label="DMO User Email"
              />
              {errors.email && (
                <p className="text-xs text-red-600 mt-1">{errors.email}</p>
              )}
            </div>

            <div>
              <label
                htmlFor="name"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                DMO Contact Person Name
              </label>
              <input
                type="text"
                name="name"
                id="name"
                value={form.name}
                autoComplete="name"
                onChange={handleChange}
                className="input input-md w-full"
                placeholder="John Doe"
                aria-label="DMO Contact Person Name"
              />
            </div>
          </div>

          <div>
            <label
              htmlFor="linked_regions"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Linked Regions
            </label>
            <Select<RegionOption, true>
              inputId="linked_regions"
              isMulti
              options={regionOptions}
              value={regionOptions.filter((option) =>
                form.linked_regions.includes(option.value)
              )}
              onChange={handleRegionsChange}
              isDisabled={isRegionsLoading}
              className="basic-multi-select"
              classNamePrefix="react-select"
              placeholder="Select regions"
              aria-label="Linked Regions"
            />
            {isRegionsError && (
              <p className="text-xs text-red-600 mt-1">
                Failed to load regions. Please try again later.
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="description"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Description <span className="text-red-500">*</span>
            </label>
            <textarea
              name="description"
              value={form.description}
              id="description"
              onChange={handleChange}
              className={clsx(
                'textarea w-full resize-none',
                errors.description && 'border-red-500 focus:ring-red-500'
              )}
              rows={3}
              placeholder="Promoting tourism and managing destinations along the North Coast."
              aria-label="Description"
            />
            {errors.description && (
              <p className="text-xs text-red-600 mt-1">{errors.description}</p>
            )}
          </div>

          <div>
            <label
              htmlFor="profile_picture"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Profile Picture
            </label>
            <input
              type="file"
              accept="image/*"
              id="profile_picture"
              onChange={(e) => {
                const file = e.target.files?.[0] ?? null;
                setProfilePicFile(file);
              }}
              autoComplete="off"
              className="file-input file-input-sm w-full"
              aria-label="Profile Picture"
            />
            {profilePicFile && (
              <p className="text-xs text-gray-500 mt-1">
                Selected: {profilePicFile.name}
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 gap-10 items-start">
            <div>
              <label
                htmlFor="language"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Language
              </label>
              <select
                name="language"
                value={form.language}
                id="language"
                onChange={handleChange}
                className="select select-md w-full"
                aria-label="Language"
              >
                <option value="en">English</option>
                <option value="fi">Finnish</option>
                <option value="de">German</option>
                <option value="sv">Swedish</option>
              </select>
            </div>
          </div>
          {showLegalConsent && (
            <div className="flex flex-col gap-3">
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="has_accepted_terms"
                  name="has_accepted_terms"
                  checked={form.has_accepted_terms}
                  onChange={handleChange}
                  className="checkbox checkbox-sm cursor-pointer mt-0.5"
                  aria-describedby={
                    errors.has_accepted_terms
                      ? 'has_accepted_terms-error'
                      : undefined
                  }
                />
                <label
                  htmlFor="has_accepted_terms"
                  className="text-sm text-gray-700"
                >
                  I accept the{' '}
                  <a
                    href={`/terms-and-conditions?lang=${form.language.toLowerCase()}`}
                    target="_blank"
                    rel="noreferrer"
                    className="font-medium text-primary underline hover:no-underline"
                  >
                    Terms & Conditions
                  </a>{' '}
                  and{' '}
                  <a
                    href={`/privacy-policy?lang=${form.language.toLowerCase()}`}
                    target="_blank"
                    rel="noreferrer"
                    className="font-medium text-primary underline hover:no-underline"
                  >
                    Privacy Policy
                  </a>
                  .
                </label>
              </div>
              {errors.has_accepted_terms && (
                <p
                  id="has_accepted_terms-error"
                  className="text-xs text-red-600"
                >
                  {errors.has_accepted_terms}
                </p>
              )}

              <label
                htmlFor="sendTermsPdf"
                className="inline-flex items-center gap-3 text-sm text-gray-700"
              >
                <input
                  type="checkbox"
                  id="sendTermsPdf"
                  name="sendTermsPdf"
                  checked={form.sendTermsPdf}
                  onChange={handleChange}
                  className="checkbox checkbox-sm cursor-pointer"
                />
                <span className="cursor-pointer">
                  Send me the Terms & Conditions on email
                </span>
              </label>
            </div>
          )}
          {!showToolbar && (
            <div className="flex justify-end gap-2.5 mt-6">
              <button
                type="button"
                className="btn btn-sm btn-secondary flex items-center gap-2"
                onClick={handleCancel}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-sm btn-primary flex items-center gap-2"
                onClick={handleSubmit}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Submit'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DMORegistrationForm;
